import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { UserCheck, Users, Clock, AlertTriangle, UserX } from 'react-feather'
import DashboardWrapper from '../../components/MainDashboardWrapper/DashboardWrapper'
import BaseTable from '../../components/BaseTable/BaseTable'
import { formatDate } from '../../helpers/dateUtils'
import { Button } from '@mantine/core'
import { getSubscriptionStats, getSubscriptionCoaches, toggleCoachBlacklist } from './helper'

const STATUS_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  active: { bg: '#EAF7EF', color: '#16A34A', label: 'Active' },
  expiringSoon: { bg: '#FEF7E6', color: '#B45309', label: 'Expiring soon' },
  expired: { bg: '#FDEAEA', color: '#DC2626', label: 'Expired' },
  unsubscribed: { bg: '#F1F5F9', color: '#64748B', label: 'Unsubscribed' },
}

const StatusPill = ({ status }: { status: string }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.unsubscribed
  return (
    <span
      className="text-xs font-bold px-2.5 py-1 rounded-full"
      style={{ background: style.bg, color: style.color }}
    >
      {style.label}
    </span>
  )
}

type StatTileProps = {
  label: string
  value: React.ReactNode
  sub?: string
  color: string
  tint: string
  icon: React.ReactNode
}

const StatTile = ({ label, value, sub, color, tint, icon }: StatTileProps) => (
  <div className="bg-white rounded-xl shadow-md p-6 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold text-gray-500">{label}</span>
      <span
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: tint, color }}
      >
        {icon}
      </span>
    </div>
    <span className="text-3xl font-extrabold text-[#0B1D3A] leading-none">{value}</span>
    {sub && <span className="text-xs text-gray-400">{sub}</span>}
  </div>
)

export default function SubscriptionDashboardPage() {
  const [stats, setStats] = useState<any>()
  const [statsError, setStatsError] = useState<string | null>(null)
  const [coaches, setCoaches] = useState<any[]>([])
  const [loadingCoaches, setLoadingCoaches] = useState(true)
  const [coachesError, setCoachesError] = useState<string | null>(null)
  const [selectedCoaches, setSelectedCoaches] = useState<any[]>([])
  const [bulkBusy, setBulkBusy] = useState(false)

  const loadCoaches = () => {
    setLoadingCoaches(true)
    setCoachesError(null)
    getSubscriptionCoaches()
      .then((res) => setCoaches(res.data || []))
      .catch((err) => setCoachesError(err || 'Failed to load coaches'))
      .finally(() => setLoadingCoaches(false))
  }

  useEffect(() => {
    getSubscriptionStats()
      .then((res) => setStats(res.data))
      .catch((err) => setStatsError(err || 'Failed to load stats'))
    loadCoaches()
  }, [])

  const onToggleBlacklist = (coach: any) => {
    const verb = coach.isBlacklisted ? 'remove' : 'add'
    if (!window.confirm(`${verb === 'add' ? 'Blacklist' : 'Un-blacklist'} ${coach.displayName || coach.email}? ${verb === 'add' ? 'They will stop receiving any automation email.' : ''}`)) return
    toggleCoachBlacklist(coach.uuid, coach.email)
      .then(loadCoaches)
      .catch((err) => window.alert(err || 'Action failed'))
  }

  const onBulkBlacklist = async () => {
    const targets = selectedCoaches.filter((c) => !c.isBlacklisted)
    if (targets.length === 0) {
      window.alert('Select at least one non-blacklisted coach first.')
      return
    }
    if (!window.confirm(`Blacklist ${targets.length} coach(es)? They will stop receiving any automation email.`)) return
    setBulkBusy(true)
    try {
      for (const coach of targets) {
        await toggleCoachBlacklist(coach.uuid, coach.email)
      }
      loadCoaches()
    } catch (err: any) {
      window.alert(err || 'Bulk blacklist failed partway through — some coaches may already be updated.')
    } finally {
      setBulkBusy(false)
    }
  }

  const columns: any[] = [
    { Header: 'Coach', accessor: 'displayName' },
    { Header: 'Email', accessor: 'email' },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }: any) => <StatusPill status={value} />,
    },
    { Header: 'Plan', accessor: 'subscriptionPlan', Cell: ({ value }: any) => value || '—' },
    {
      Header: 'Expiry Date',
      accessor: 'expiryDate',
      Cell: (cellProps: any) => {
        if (cellProps.row.original.isLifetime) return 'Lifetime'
        return cellProps.value ? formatDate(new Date(cellProps.value).toISOString()) : '—'
      },
    },
    {
      Header: 'Automation Emails',
      id: 'blacklist',
      Cell: (cellProps: any) => {
        const coach = cellProps.row.original
        return (
          <div className="flex items-center gap-2">
            {coach.isBlacklisted && (
              <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: '#FDEAEA', color: '#DC2626' }}>
                Blacklisted
              </span>
            )}
            <Button size="xs" variant="outline" color={coach.isBlacklisted ? 'gray' : 'red'} onClick={() => onToggleBlacklist(coach)}>
              {coach.isBlacklisted ? 'Un-blacklist' : 'Blacklist'}
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <DashboardWrapper page="subscriptions" title="Subscriptions">
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Coach subscription health, from live Sharetribe subscription data.
          </p>
          <Link
            to="/subscriptions/automation"
            className="text-sm font-bold no-underline text-[#3882F6]"
          >
            Manage automation →
          </Link>
        </div>

        {statsError && (
          <div className="bg-white border border-red-200 rounded-xl p-4 text-sm text-red-600">
            Couldn't load stats: {statsError}
          </div>
        )}

        <div className="grid grid-cols-5 gap-4">
          <StatTile
            label="Total Subscribed"
            value={stats ? stats.totalSubscribedCoaches : '—'}
            sub="Active + expiring soon"
            color="#3882F6"
            tint="#EAF1FE"
            icon={<UserCheck size={18} />}
          />
          <StatTile
            label="Active"
            value={stats ? stats.activeSubscriptions : '—'}
            color="#16A34A"
            tint="#EAF7EF"
            icon={<Users size={18} />}
          />
          <StatTile
            label="Expiring Soon"
            value={stats ? stats.expiringSubscriptions : '—'}
            sub={stats ? `Within ${stats.expiringSoonWindowDays} days` : undefined}
            color="#B45309"
            tint="#FEF7E6"
            icon={<Clock size={18} />}
          />
          <StatTile
            label="Expired"
            value={stats ? stats.expiredSubscriptions : '—'}
            color="#DC2626"
            tint="#FDEAEA"
            icon={<AlertTriangle size={18} />}
          />
          <StatTile
            label="Unsubscribed"
            value={stats ? stats.unsubscribedCoaches : '—'}
            color="#64748B"
            tint="#F1F5F9"
            icon={<UserX size={18} />}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-base font-bold">Coaches</p>
            <Button
              size="xs"
              color="red"
              variant="outline"
              disabled={bulkBusy || selectedCoaches.filter((c) => !c.isBlacklisted).length === 0}
              onClick={onBulkBlacklist}
            >
              {bulkBusy ? 'Blacklisting…' : `Blacklist Selected (${selectedCoaches.filter((c) => !c.isBlacklisted).length})`}
            </Button>
          </div>
          {coachesError && (
            <div className="bg-white border border-red-200 rounded-xl p-4 text-sm text-red-600 mb-2">
              Couldn't load coaches: {coachesError}{' '}
              <button type="button" className="underline" onClick={loadCoaches}>
                Retry
              </button>
            </div>
          )}
          <BaseTable
            data={coaches}
            columns={columns}
            loading={loadingCoaches}
            initialPageSize={20}
            isSelectable
            onSelectRows={setSelectedCoaches}
          />
        </div>
      </div>
    </DashboardWrapper>
  )
}
